import { Injectable } from '@angular/core';
import { from, Observable, ReplaySubject, Subject, throwError } from 'rxjs';
import detectEthereumProvider from '@metamask/detect-provider';
import { switchMap, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { TilesService } from './tiles.service';
import { PopupsManagerService } from './popups-manager.service';
import {
  PurchaseInfoPopupComponent,
  PurchaseInfoType,
} from '../components/purchase-info-popup/purchase-info-popup.component';
import { HowToComponent } from '../components/how-to/how-to.component';

export const SkipAuthHeader = 'skipAuthHeader';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public publicId?: string;
  private publicIdSub: Subject<string> = new ReplaySubject<string>(1);
  public publicIdChange$: Observable<string> = this.publicIdSub.asObservable();
  private signInRequestSub: Subject<void> = new Subject<void>();
  public signInRequest$ = this.signInRequestSub.asObservable();
  private ethereum: any;

  constructor(
    private http: HttpClient,
    private tilesService: TilesService,
    private popupsManagerService: PopupsManagerService
  ) {
    if (localStorage.getItem('token')) {
      (window.ethereum as any)
        .request({
          method: 'eth_requestAccounts',
        })
        .then((res: string[]) => {
          [this.publicId] = res;
          this.publicIdSub.next(this.publicId);
        });
    }
  }

  public async checkNetworkAndSwitch(): Promise<any> {
    const ethereum = this.ethereum || (await detectEthereumProvider());
    if (
      ethereum.networkVersion.toString(16) !==
      environment.metamaskChannel.chainId
    ) {
      await ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x4' }],
      });
    }
    return Promise.resolve();
  }

  public requestSignIn(): void {
    this.signInRequestSub.next();
  }

  public signInWithMetamask(): Observable<any> {
    let nonce: string;
    return from(detectEthereumProvider()).pipe(
      switchMap(async (provider) => await this.requestAccounts(provider)),
      switchMap(async () => await this.checkNetworkAndSwitch()),
      switchMap(() =>
        this.http.get(`${environment.baseApi}/client/auth/nonce`)
      ),
      switchMap(async (resp: any) => {
        nonce = resp.nonce;
        return this.personalSign(nonce);
      }),
      switchMap((sig: string) => this.clientAuthRequest(sig, nonce))
    );
  }

  private personalSign(nonce: string): Promise<any> {
    return this.ethereum.request({
      method: 'personal_sign',
      params: [`0x${this.toHex(nonce)}`, this.ethereum.selectedAddress],
    });
  }

  private requestAccounts(provider: any): Promise<any> | Observable<never> {
    if (!provider) {
      this.popupsManagerService.show(HowToComponent, {
        className: 'overlay',
      });
      return throwError('Please install MetaMask');
    }
    this.ethereum = provider;
    return provider.request({ method: 'eth_requestAccounts' });
  }

  private clientAuthRequest(sig: string, nonce: string): Observable<any> {
    return this.http
      .post(
        `${environment.baseApi}/client/auth`,
        {
          publicAddress: this.ethereum.selectedAddress,
          signature: sig,
          nonce,
        },
        { observe: 'response' }
      )
      .pipe(
        tap((res) => this.checkToken(res)),
        switchMap(() => this.tilesService.fetchCartItems()),
        tap(() => {
          this.publicId = this.ethereum.selectedAddress;
          this.publicIdSub.next(this.publicId);
        })
      );
  }

  private checkToken(res: any): void {
    const token = res.headers.get('X-Auth-Token');
    if ((res.body as any).hasNotification) {
      this.popupsManagerService.show(PurchaseInfoPopupComponent, {
        inputs: {
          type: PurchaseInfoType.Error,
          info: {
            title: 'Unavailable Tiles!',
            text: 'One or more tiles are removed from your cart',
            subText: 'as they are no longer available.',
          },
        },
        className: 'overlay',
      });
    }
    if (token) {
      localStorage.setItem('token', token);
    }
  }

  private toHex(stringToConvert: string) {
    return stringToConvert
      .split('')
      .map((c) => c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('');
  }
}
